import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Crown, Sparkles, ShieldCheck, ArrowLeft, Zap, Youtube } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";

const PERKS = [
  "Your personalized AI roadmap (built from a 60-sec quiz)",
  "Every curated lesson unlocked",
  "AI lesson summaries & quizzes",
  "Practice tasks & real projects",
  "Tool-specific courses (ChatGPT, Midjourney…)",
  "Prompt templates + downloadable notes",
  '"Ask AI" doubt-solver on every lesson',
  "Progress analytics & a shareable roadmap",
];

function PlanCard({ label, price, per, sub, highlight, onClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className={`relative rounded-3xl border p-7 ${highlight ? "border-primary bg-card shadow-xl ring-2 ring-primary/30" : "border-border bg-card shadow-sm"}`}
    >
      {highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground shadow-md">Best value · save 50%</span>
      )}
      <p className="font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
      <div className="mt-3 flex items-end gap-1">
        <span className="font-heading text-5xl font-black tracking-tighter">₹{price}</span>
        <span className="mb-1.5 text-muted-foreground">/{per}</span>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{sub}</p>
      <Button onClick={onClick} className={`mt-6 w-full rounded-2xl py-6 text-base font-bold ${highlight ? "bg-primary text-primary-foreground" : "bg-foreground text-background hover:bg-foreground/90"}`} data-testid={`pricing-cta-${label.toLowerCase()}`}>
        Get started <Crown className="h-4 w-4" />
      </Button>
    </motion.div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const go = () => {
    if (!user) return navigate("/quiz");
    if (user.is_subscribed || user.role === "admin") return navigate("/dashboard");
    return navigate(user.has_path ? "/subscribe" : "/quiz");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="pricing-page">
      <header className="border-b border-border bg-card/85 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground group-hover:rotate-6 transition-transform">
              <Zap className="h-5 w-5 fill-current" />
            </span>
            <span className="font-heading font-extrabold text-lg tracking-tight">AI Pathshaalaa</span>
          </Link>
          {user ? (
            <Button size="sm" variant="outline" onClick={() => navigate("/dashboard")} data-testid="pricing-dashboard">Dashboard</Button>
          ) : (
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Home</Link>
          )}
        </div>
      </header>

      <main className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 rounded-full px-3 py-1.5">
            <Sparkles className="h-3.5 w-3.5" /> Pricing
          </span>
          <h1 className="mt-5 font-heading font-extrabold tracking-tight text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
            The videos are free.<br />You're paying for the <span className="text-primary">course.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            Every YouTube video on AI Pathshaalaa is free and always playable. You subscribe for the layer on top —
            the <span className="font-semibold text-foreground">AI-curated roadmap, summaries, quizzes, projects, notes and personalization.</span> Never for the videos themselves.
          </p>
          <Link to="/youtube-compliance" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground" data-testid="pricing-compliance-link">
            <ShieldCheck className="h-4 w-4 text-success" /> How we stay compliant with YouTube
          </Link>
        </div>

        <div className="mt-10 max-w-3xl mx-auto flex items-start gap-2.5 rounded-2xl border border-emerald-300/60 bg-emerald-50 dark:bg-emerald-500/10 px-5 py-4" data-testid="pricing-refund-banner">
          <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0 text-emerald-600" />
          <p className="text-sm text-emerald-800 dark:text-emerald-300 leading-relaxed">
            <span className="font-bold">7-day no-questions-asked refund policy.</span> If you don't find it value for
            money, it's just one click — and you get your money back. No forms, no calls, no questions.
          </p>
        </div>

        <div className="mt-6 grid gap-5 md:grid-cols-2 max-w-3xl mx-auto items-center">
          <PlanCard label="Monthly" price="555" per="month" sub="Billed every month. Cancel anytime." onClick={go} />
          <PlanCard label="Yearly" price="3,333" per="year" sub="Just ₹278/month, billed yearly." highlight onClick={go} />
        </div>

        <div className="mt-10 max-w-3xl mx-auto rounded-3xl border border-border bg-card shadow-sm p-6 sm:p-8">
          <h2 className="font-heading font-bold text-lg">Everything included in both plans</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {PERKS.map((p) => (
              <div key={p} className="flex items-start gap-2.5">
                <span className="grid place-items-center h-5 w-5 shrink-0 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 mt-0.5"><Check className="h-3 w-3" /></span>
                <span className="text-sm text-foreground/90">{p}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 max-w-3xl mx-auto rounded-2xl border border-border bg-primary/5 p-5 flex items-start gap-3">
          <Youtube className="h-5 w-5 text-[#FF0000] shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Your subscription pays for our curation, summaries, quizzes, structure, projects and personalized roadmap — never for the videos.</span> The
            YouTube videos remain free on YouTube, the property of their creators, and always play in the official YouTube player. Secure payments by Razorpay; cancel anytime.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
